/**
 * WebSocket event contracts.
 */

import type { ChatMessage, SendMessageAck, SendMessageInput, ReactInput, ReactionUpdate } from './messages';
import type { SessionIdentity } from './session';
import type { ChatErrorPayload } from './errors';
import type { VipGrantedPayload } from './eligibility';
import type { RateLimitedPayload } from './moderation';
import type { UserBannedPayload } from './bans';

export const WS_NAMESPACE = '/chat';

export const WsEvents = {
  // Server -> Client
  SESSION_READY: 'session:ready',
  HISTORY_INITIAL: 'history:initial',
  MESSAGE_NEW: 'message:new',
  MESSAGE_ACK: 'message:ack',
  MESSAGE_BLOCKED: 'message:blocked',
  MESSAGE_REACTION: 'message:reaction',
  PRESENCE_COUNT: 'presence:count',
  PRESENCE_ONLINE: 'presence:online',
  PRESENCE_OFFLINE: 'presence:offline',
  TYPING_USER_STARTED: 'typing:user-started',
  TYPING_USER_STOPPED: 'typing:user-stopped',
  VIP_GRANTED: 'vip:granted',
  NOTIF_VIP_ACTIVATED: 'notif:vip-activated',
  NOTIF_RATE_LIMITED: 'notif:rate-limited',
  USER_BANNED: 'user:banned',
  KICKED: 'kicked',
  ERROR: 'error',

  // Client -> Server
  MESSAGE_SEND: 'message:send',
  MESSAGE_REACT: 'message:react',
  TYPING_START: 'typing:start',
  TYPING_STOP: 'typing:stop',
  NOTIF_SEEN: 'notif:seen',
} as const;

export interface ServerToClientEvents {
  'session:ready': (payload: SessionIdentity) => void;
  'history:initial': (payload: { items: ChatMessage[] }) => void;
  'message:new': (payload: ChatMessage) => void;
  'message:ack': (payload: SendMessageAck) => void;
  'message:blocked': (payload: { clientNonce: string; reason: string }) => void;
  'message:reaction': (payload: ReactionUpdate) => void;
  'presence:count': (payload: { online: number }) => void;
  'presence:online': (payload: { userId: string; name: string }) => void;
  'presence:offline': (payload: { userId: string; name?: string }) => void;
  'typing:user-started': (payload: { userId: string; name: string }) => void;
  'typing:user-stopped': (payload: { userId: string }) => void;
  'vip:granted': (payload: VipGrantedPayload) => void;
  'notif:vip-activated': (payload: NotifVipActivatedPayload) => void;
  'notif:rate-limited': (payload: RateLimitedPayload) => void;
  'user:banned': (payload: UserBannedPayload) => void;
  kicked: (payload: { reason: string }) => void;
  error: (payload: ChatErrorPayload) => void;
}

export interface NotifVipActivatedPayload {
  /** Mongo notification _id (string). Undefined for the live in-flight push that's not yet persisted. */
  notificationId?: string;
  userId: string;
  trigger: 'deposit-met' | 'manual-grant';
  at: string;
}

export interface ClientToServerEvents {
  'message:send': (
    payload: SendMessageInput,
    ack: (response: SendMessageAck | { error: ChatErrorPayload }) => void,
  ) => void;
  'message:react': (payload: ReactInput) => void;
  'typing:start': () => void;
  'typing:stop': () => void;
  'notif:seen': (payload: { notificationId: string }) => void;
}
